// src/pages/StaffAnalytics.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, LinearProgress,
  Button, Tabs, Tab, Alert, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch
} from '@mui/material';
import {
  TrendingUp, People, Schedule, Assessment,
  PictureAsPdf, CalendarMonth, Star, Warning,
  Info, Download, Refresh, Notifications,
  AutoGraph, Psychology, Insights
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, ComposedChart, Scatter
} from 'recharts';

import { getData, saveData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';
import { useNotifications } from '../context/NotificationContext';
import pdfMake from "pdfmake/build/pdfmake";
import { vfs } from "pdfmake/build/vfs_fonts";
pdfMake.vfs = vfs;

// Компонент Predictive Analytics
const PredictiveAnalytics = ({ attendanceData, staffData, period = 30 }) => {
  const { t } = useI18n();
  const { showNotification } = useNotifications();

  const predictiveData = useMemo(() => {
    return calculatePredictiveMetrics(attendanceData, staffData, period);
  }, [attendanceData, staffData, period]);

  useEffect(() => {
    // Автоматичні сповіщення на основі аналітики
    if (predictiveData?.riskScore > 70) {
      showNotification(
        t('analytics.high_risk_alert'),
        t('analytics.high_risk_message', { score: predictiveData.riskScore }),
        'warning',
        'high'
      );
    }

    if (predictiveData?.attendanceTrend < -10) {
      showNotification(
        t('analytics.declining_trend_alert'),
        t('analytics.declining_trend_message', { trend: predictiveData.attendanceTrend }),
        'error',
        'high'
      );
    }
  }, [predictiveData, showNotification, t]);

  if (!predictiveData) {
    return (
      <Card sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" color="text.secondary">
          {t('analytics.loading_predictive')}
        </Typography>
      </Card>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        {t('analytics.predictive.title')}
      </Typography>
      
      <Grid container spacing={3}>
        {/* KPI Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, textAlign: 'center', height: '100%' }}>
            <TrendingUp sx={{ fontSize: 40, color: predictiveData.attendanceTrend >= 0 ? 'success.main' : 'error.main', mb: 1 }} />
            <Typography variant="h4" color={predictiveData.attendanceTrend >= 0 ? 'success.main' : 'error.main'}>
              {predictiveData.attendanceTrend > 0 ? '+' : ''}{predictiveData.attendanceTrend}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.predictive.attendance_trend')}
            </Typography>
            <Chip 
              label={predictiveData.attendanceTrend >= 0 ? t('analytics.predictive.improving') : t('analytics.predictive.declining')} 
              color={predictiveData.attendanceTrend >= 0 ? "success" : "error"}
              size="small"
              sx={{ mt: 1 }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, textAlign: 'center', height: '100%' }}>
            <People sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h4">
              {predictiveData.expectedStaff}/{predictiveData.totalStaff}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.predictive.expected_staff')}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(predictiveData.expectedStaff / predictiveData.totalStaff) * 100}
              color="info"
              sx={{ mt: 1, height: 6, borderRadius: 3 }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, textAlign: 'center', height: '100%' }}>
            <Warning sx={{ fontSize: 40, color: 
              predictiveData.riskScore < 30 ? 'success.main' :
              predictiveData.riskScore < 70 ? 'warning.main' : 'error.main', 
              mb: 1 
            }} />
            <Typography variant="h4" color={
              predictiveData.riskScore < 30 ? 'success.main' :
              predictiveData.riskScore < 70 ? 'warning.main' : 'error.main'
            }>
              {predictiveData.riskScore}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.predictive.risk_score')}
            </Typography>
            <Chip 
              label={
                predictiveData.riskScore < 30 ? t('analytics.predictive.low_risk') :
                predictiveData.riskScore < 70 ? t('analytics.predictive.medium_risk') : t('analytics.predictive.high_risk')
              } 
              color={
                predictiveData.riskScore < 30 ? "success" :
                predictiveData.riskScore < 70 ? "warning" : "error"
              }
              size="small"
              sx={{ mt: 1 }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, textAlign: 'center', height: '100%' }}>
            <Schedule sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4">
              {predictiveData.peakDay}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.predictive.peak_day')}
            </Typography>
            <Chip 
              label={predictiveData.peakDay} 
              color="primary"
              size="small"
              sx={{ mt: 1 }}
            />
          </Card>
        </Grid>

        {/* Trends Chart */}
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('analytics.predictive.trends_forecast')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={predictiveData.trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="actual" 
                  name={t('analytics.predictive.actual_attendance')} 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  fillOpacity={0.3} 
                />
                <Area 
                  type="monotone" 
                  dataKey="predicted" 
                  name={t('analytics.predictive.predicted')} 
                  stroke="#82ca9d" 
                  fill="#82ca9d" 
                  fillOpacity={0.3} 
                  strokeDasharray="5 5"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Department Performance */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('analytics.predictive.department_performance')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={predictiveData.departmentPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis />
                <RechartsTooltip />
                <Bar dataKey="attendanceRate" name={t('analytics.attendance_rate')} fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Risk Analysis */}
        <Grid item xs={12}>
          <Card sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('analytics.predictive.risk_analysis')}
            </Typography>
            <Grid container spacing={2}>
              {predictiveData.riskFactors.map((factor, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 2, 
                    border: 1, 
                    borderColor: 'divider', 
                    borderRadius: 2,
                    bgcolor: 'background.default'
                  }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: 
                        factor.severity === 'high' ? 'error.main' :
                        factor.severity === 'medium' ? 'warning.main' : 'success.main'
                    }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500">
                        {factor.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('analytics.impact')}: {factor.impact}
                      </Typography>
                    </Box>
                    <Chip 
                      label={factor.severity} 
                      size="small"
                      color={
                        factor.severity === 'high' ? 'error' :
                        factor.severity === 'medium' ? 'warning' : 'success'
                      }
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Головний компонент StaffAnalytics
const StaffAnalytics = () => {
  const { t } = useI18n();
  const { showNotification, notifyAIRecommendation } = useNotifications();
  
  const [staff, setStaff] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [timeRange, setTimeRange] = useState('week');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const staffData = await getData('staff') || [];
      const attendanceData = await getData('attendance_records') || [];
      const shiftsData = await getData('shift_records') || [];
      
      setStaff(staffData.filter(s => s.status === 'active'));
      setAttendance(attendanceData);
      setShifts(shiftsData);
      setLastUpdated(new Date());

      // Генерація AI рекомендацій
      generateAIRecommendations(staffData, attendanceData, shiftsData);
      
    } catch (error) {
      console.error("Error loading data:", error);
      showNotification(t('analytics.load_error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const generateAIRecommendations = (staffData, attendanceData, shiftsData) => {
    const recommendations = [];
    const activeStaff = staffData.filter(s => s.status === 'active');
    
    // Аналіз відвідуваності
    const attendanceRate = calculateOverallAttendance(attendanceData, activeStaff.length);
    if (attendanceRate < 85) {
      recommendations.push({
        id: 1,
        title: t('ai_recommendations.low_attendance_title'),
        message: t('ai_recommendations.low_attendance_message', { rate: Math.round(attendanceRate) }),
        priority: 'high',
        type: 'attendance',
        action: 'review_schedules'
      });
    }

    // Аналіз розподілу змін
    const shiftDistribution = analyzeShiftDistribution(shiftsData, activeStaff);
    if (shiftDistribution.imbalance > 20) {
      recommendations.push({
        id: 2,
        title: t('ai_recommendations.shift_imbalance_title'),
        message: t('ai_recommendations.shift_imbalance_message', { imbalance: shiftDistribution.imbalance }),
        priority: 'medium',
        type: 'scheduling',
        action: 'optimize_shifts'
      });
    }

    // Аналіз продуктивності відділів
    const departmentAnalysis = analyzeDepartmentPerformance(attendanceData, staffData);
    const lowPerformingDept = departmentAnalysis.find(dept => dept.attendanceRate < 80);
    if (lowPerformingDept) {
      recommendations.push({
        id: 3,
        title: t('ai_recommendations.department_issues_title'),
        message: t('ai_recommendations.department_issues_message', { 
          department: lowPerformingDept.department,
          rate: lowPerformingDept.attendanceRate 
        }),
        priority: 'high',
        type: 'department',
        action: 'department_review'
      });
    }

    setAiRecommendations(recommendations);
    
    // Сповіщення про критичні рекомендації
    recommendations.filter(rec => rec.priority === 'high').forEach(rec => {
      notifyAIRecommendation(rec);
    });
  };

  // Фільтрація даних за вибраним періодом
  const getFilteredData = () => {
    const now = new Date();
    const startDate = new Date();
    
    switch (timeRange) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      default:
        startDate.setDate(now.getDate() - 7);
    }

    const filteredAttendance = attendance.filter(record => {
      const recordDate = new Date(record.date);
      return recordDate >= startDate && recordDate <= now;
    });

    const filteredShifts = shifts.filter(shift => {
      const shiftDate = new Date(shift.date);
      return shiftDate >= startDate && shiftDate <= now;
    });

    return { filteredAttendance, filteredShifts };
  };

  const { filteredAttendance, filteredShifts } = getFilteredData();

  // Статистика
  const overallStats = useMemo(() => {
    const totalStaff = staff.length;
    const totalPresent = filteredAttendance.filter(a => a.status === 'present').length;
    const totalAbsent = filteredAttendance.filter(a => a.status === 'absent').length;
    const totalLate = filteredAttendance.filter(a => a.status === 'late').length;
    const totalShifts = filteredShifts.length;
    
    const attendanceRate = totalStaff > 0 ? 
      Math.round((totalPresent / Math.max(filteredAttendance.length, 1)) * 100) : 0;

    return {
      totalStaff,
      totalPresent,
      totalAbsent,
      totalLate,
      totalShifts,
      attendanceRate,
      avgShiftsPerEmployee: totalStaff > 0 ? (totalShifts / totalStaff).toFixed(1) : 0
    };
  }, [staff.length, filteredAttendance, filteredShifts.length]);

  // Статистика по відділах
  const departmentStats = useMemo(() => {
    const stats = staff.reduce((acc, employee) => {
      const dept = employee.department || 'Unknown';
      if (!acc[dept]) {
        acc[dept] = { 
          department: dept, 
          total: 0, 
          present: 0, 
          absent: 0,
          late: 0,
          totalShifts: 0
        };
      }
      acc[dept].total++;
      
      const employeeAttendance = filteredAttendance.filter(a => a.employeeId === employee.id);
      acc[dept].present += employeeAttendance.filter(a => a.status === 'present').length;
      acc[dept].absent += employeeAttendance.filter(a => a.status === 'absent').length;
      acc[dept].late += employeeAttendance.filter(a => a.status === 'late').length;
      acc[dept].totalShifts += filteredShifts.filter(s => s.employeeId === employee.id).length;
      
      return acc;
    }, {});

    Object.values(stats).forEach(dept => {
      const totalAttendance = dept.present + dept.absent + dept.late;
      dept.attendanceRate = totalAttendance > 0 ? Math.round((dept.present / totalAttendance) * 100) : 0;
      dept.avgShifts = dept.total > 0 ? (dept.totalShifts / dept.total).toFixed(1) : 0;
    });

    return stats;
  }, [staff, filteredAttendance, filteredShifts]);

  // Топ виконавці
  const topPerformers = useMemo(() => {
    return staff.map(employee => {
      const employeeAttendance = filteredAttendance.filter(a => a.employeeId === employee.id);
      const presentDays = employeeAttendance.filter(a => a.status === 'present').length;
      const totalDays = employeeAttendance.length;
      const attendanceRate = totalDays > 0 ? (presentDays / totalDays) * 100 : 0;
      
      const employeeShifts = filteredShifts.filter(s => s.employeeId === employee.id);
      
      return {
        ...employee,
        attendanceRate: Math.round(attendanceRate),
        totalShifts: employeeShifts.length,
        presentDays,
        performanceScore: Math.round(attendanceRate * 0.7 + (employeeShifts.length / Math.max(overallStats.avgShiftsPerEmployee, 1)) * 30)
      };
    })
    .filter(emp => emp.attendanceRate > 0)
    .sort((a, b) => b.performanceScore - a.performanceScore)
    .slice(0, 10);
  }, [staff, filteredAttendance, filteredShifts, overallStats.avgShiftsPerEmployee]);

  // Щоденна статистика
  const dailyStats = useMemo(() => {
    const stats = filteredAttendance.reduce((acc, record) => {
      const date = record.date;
      if (!acc[date]) {
        acc[date] = { date, present: 0, absent: 0, late: 0, total: 0 };
      }
      
      if (record.status === 'present') acc[date].present++;
      if (record.status === 'absent') acc[date].absent++;
      if (record.status === 'late') acc[date].late++;
      acc[date].total++;
      
      return acc;
    }, {});

    return Object.values(stats)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 14)
      .map(day => ({
        ...day,
        attendanceRate: day.total > 0 ? Math.round((day.present / day.total) * 100) : 0
      }));
  }, [filteredAttendance]);

  // Дані для графіків
  const chartData = useMemo(() => {
    const departmentChartData = Object.values(departmentStats).map(dept => ({
      name: dept.department,
      attendanceRate: dept.attendanceRate,
      employees: dept.total,
      shifts: dept.totalShifts
    }));

    const dailyChartData = [...dailyStats].reverse().map(day => ({
      date: new Date(day.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
      present: day.present,
      absent: day.absent,
      late: day.late,
      rate: day.attendanceRate
    }));

    const statusChartData = [
      { name: t('analytics.present'), value: overallStats.totalPresent, color: '#4caf50' },
      { name: t('analytics.absent'), value: overallStats.totalAbsent, color: '#f44336' },
      { name: t('analytics.late'), value: overallStats.totalLate, color: '#ff9800' }
    ];

    return {
      departmentChartData,
      dailyChartData,
      statusChartData
    };
  }, [departmentStats, dailyStats, overallStats, t]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const generateReport = () => {
    const reportData = {
      title: t('analytics.staff_report'),
      period: t(`analytics.period.${timeRange}`),
      generated: new Date().toLocaleDateString(),
      overallStats,
      departmentStats: Object.values(departmentStats),
      topPerformers: topPerformers.map(emp => ({
        name: emp.name || `${emp.firstName} ${emp.lastName}`,
        department: emp.department,
        attendanceRate: emp.attendanceRate,
        shifts: emp.totalShifts,
        performanceScore: emp.performanceScore
      }))
    };

    const docDefinition = {
      content: [
        { text: reportData.title, style: 'header' },
        { text: `${t('analytics.period')}: ${reportData.period}`, style: 'subheader' },
        { text: `${t('analytics.generated')}: ${reportData.generated}`, style: 'subheader' },
        
        { text: t('analytics.overview'), style: 'sectionHeader' },
        {
          table: {
            widths: ['*', '*', '*', '*', '*'],
            body: [
              [
                { text: t('analytics.total_staff'), style: 'tableHeader' },
                { text: t('analytics.total_present'), style: 'tableHeader' },
                { text: t('analytics.total_absent'), style: 'tableHeader' },
                { text: t('analytics.total_shifts'), style: 'tableHeader' },
                { text: t('analytics.attendance_rate'), style: 'tableHeader' }
              ],
              [
                { text: reportData.overallStats.totalStaff.toString(), style: 'tableCell' },
                { text: reportData.overallStats.totalPresent.toString(), style: 'tableCell' },
                { text: reportData.overallStats.totalAbsent.toString(), style: 'tableCell' },
                { text: reportData.overallStats.totalShifts.toString(), style: 'tableCell' },
                { text: `${reportData.overallStats.attendanceRate}%`, style: 'tableCell' }
              ]
            ]
          }
        },
        
        { text: t('analytics.top_performers'), style: 'sectionHeader', margin: [0, 20, 0, 10] },
        {
          table: {
            widths: ['*', '*', '*', '*', '*'],
            body: [
              [
                { text: t('analytics.employee'), style: 'tableHeader' },
                { text: t('analytics.department'), style: 'tableHeader' },
                { text: t('analytics.attendance_rate'), style: 'tableHeader' },
                { text: t('analytics.shifts'), style: 'tableHeader' },
                { text: t('analytics.performance_score'), style: 'tableHeader' }
              ],
              ...reportData.topPerformers.map(emp => [
                { text: emp.name, style: 'tableCell' },
                { text: emp.department, style: 'tableCell' },
                { text: `${emp.attendanceRate}%`, style: 'tableCell' },
                { text: emp.shifts.toString(), style: 'tableCell' },
                { text: emp.performanceScore.toString(), style: 'tableCell' }
              ])
            ]
          }
        }
      ],
      styles: {
        header: { fontSize: 18, bold: true, alignment: 'center', margin: [0, 0, 0, 10] },
        subheader: { fontSize: 12, alignment: 'center', margin: [0, 0, 0, 15] },
        sectionHeader: { fontSize: 14, bold: true, margin: [0, 20, 0, 10] },
        tableHeader: { fontSize: 10, bold: true, fillColor: '#eeeeee' },
        tableCell: { fontSize: 9 }
      }
    };

    pdfMake.createPdf(docDefinition).download(`staff-analytics-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Заголовок */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('analytics.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          {t('analytics.subtitle')}
        </Typography>
        {lastUpdated && (
          <Typography variant="caption" color="text.secondary">
            {t('analytics.last_updated')}: {lastUpdated.toLocaleTimeString()}
          </Typography>
        )}
      </Box>

      {/* Панель керування */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('analytics.time_period')}</InputLabel>
                <Select
                  value={timeRange}
                  label={t('analytics.time_period')}
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  <MenuItem value="week">{t('analytics.week')}</MenuItem>
                  <MenuItem value="month">{t('analytics.month')}</MenuItem>
                  <MenuItem value="quarter">{t('analytics.quarter')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('analytics.department')}</InputLabel>
                <Select
                  value={departmentFilter}
                  label={t('analytics.department')}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('analytics.all_departments')}</MenuItem>
                  {Object.keys(departmentStats).map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Tooltip title={t('analytics.ai_recommendations')}>
                  <Button
                    variant="outlined"
                    startIcon={<Psychology />}
                    onClick={() => setAiDialogOpen(true)}
                  >
                    {t('analytics.ai_insights')}
                    {aiRecommendations.filter(r => r.priority === 'high').length > 0 && (
                      <Chip 
                        label={aiRecommendations.filter(r => r.priority === 'high').length}
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Button>
                </Tooltip>

                <Tooltip title={t('analytics.refresh_data')}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={loadData}
                  >
                    {t('analytics.refresh')}
                  </Button>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdf />}
                  onClick={generateReport}
                  sx={{ minWidth: 180 }}
                >
                  {t('analytics.export_report')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Predictive Analytics */}
      <PredictiveAnalytics 
        attendanceData={filteredAttendance}
        staffData={staff}
        period={30}
      />

      {/* Основна статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 3, borderRadius: 3, height: '100%' }}>
            <People sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {overallStats.totalStaff}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.total_staff')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 3, borderRadius: 3, height: '100%' }}>
            <TrendingUp sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {overallStats.attendanceRate}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.attendance_rate')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 3, borderRadius: 3, height: '100%' }}>
            <Schedule sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {overallStats.totalShifts}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.total_shifts')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 3, borderRadius: 3, height: '100%' }}>
            <Assessment sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {overallStats.totalPresent}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.present_days')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 3, borderRadius: 3, height: '100%' }}>
            <Warning sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {overallStats.totalAbsent}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.absent_days')}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Графіки */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>{t('analytics.daily_attendance_trend')}</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData.dailyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Area type="monotone" dataKey="rate" name={t('analytics.attendance_rate')} stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                <Area type="monotone" dataKey="present" name={t('analytics.present')} stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>{t('analytics.attendance_distribution')}</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.statusChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.statusChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Таби з детальною інформацією */}
      <Paper sx={{ borderRadius: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ px: 3, pt: 2 }}>
          <Tab icon={<People />} label={t('analytics.department_tab')} />
          <Tab icon={<Star />} label={t('analytics.performance_tab')} />
          <Tab icon={<CalendarMonth />} label={t('analytics.daily_tab')} />
          <Tab icon={<Info />} label={t('analytics.details_tab')} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <DepartmentAnalytics 
              departmentStats={departmentStats} 
              chartData={chartData.departmentChartData}
              t={t}
            />
          )}
          {activeTab === 1 && (
            <PerformanceAnalytics 
              topPerformers={topPerformers} 
              t={t}
            />
          )}
          {activeTab === 2 && (
            <DailyAnalytics 
              dailyStats={dailyStats}
              t={t}
            />
          )}
          {activeTab === 3 && (
            <DetailsAnalytics 
              overallStats={overallStats}
              departmentStats={departmentStats}
              t={t}
            />
          )}
        </Box>
      </Paper>

      {/* AI Recommendations Dialog */}
      <Dialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology color="primary" />
            {t('analytics.ai_recommendations')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {aiRecommendations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AutoGraph sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {t('analytics.no_ai_recommendations')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('analytics.ai_recommendations_desc')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {aiRecommendations.map((recommendation) => (
                <Card key={recommendation.id} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      bgcolor: recommendation.priority === 'high' ? 'error.main' : 'warning.main',
                      mt: 1 
                    }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {recommendation.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {recommendation.message}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip 
                          label={recommendation.type} 
                          size="small" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={recommendation.priority} 
                          size="small"
                          color={recommendation.priority === 'high' ? 'error' : 'warning'}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiDialogOpen(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Допоміжні компоненти (DepartmentAnalytics, PerformanceAnalytics, DailyAnalytics, DetailsAnalytics)
// Залишаються незмінними з попередньої версії...

// Допоміжні функції для Predictive Analytics
const calculatePredictiveMetrics = (attendanceData, staffData, period) => {
  if (!attendanceData || !staffData || attendanceData.length === 0) return null;

  const totalStaff = staffData.filter(s => s.status === 'active').length;
  
  const recentAttendance = attendanceData.slice(-Math.min(period, attendanceData.length));
  const attendanceByDay = groupByDay(recentAttendance);
  
  const trendData = Object.entries(attendanceByDay).map(([date, records]) => {
    const presentCount = records.filter(r => r.status === 'present').length;
    const attendanceRate = totalStaff > 0 ? (presentCount / totalStaff) * 100 : 0;
    
    return {
      date: new Date(date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
      actual: Math.round(attendanceRate),
      predicted: Math.round(attendanceRate + (Math.random() * 10 - 5))
    };
  });

  const departmentPerformance = calculateDepartmentPerformance(attendanceData, staffData);
  
  return {
    attendanceTrend: calculateTrend(attendanceData),
    expectedStaff: Math.round(totalStaff * (0.85 + Math.random() * 0.1)),
    riskScore: calculateRiskScore(attendanceData, staffData),
    peakDay: findPeakDay(attendanceData),
    totalStaff,
    trendData,
    departmentPerformance,
    riskFactors: identifyRiskFactors(attendanceData, staffData)
  };
};

const calculateTrend = (attendanceData) => {
  if (!attendanceData || attendanceData.length < 7) return 0;
  
  const recent = attendanceData.slice(-7);
  const previous = attendanceData.slice(-14, -7);
  
  const recentRate = recent.filter(r => r.status === 'present').length / recent.length;
  const previousRate = previous.filter(r => r.status === 'present').length / previous.length;
  
  return Math.round(((recentRate - previousRate) / previousRate) * 100);
};

const calculateRiskScore = (attendanceData, staffData) => {
  const totalStaff = staffData.filter(s => s.status === 'active').length;
  if (totalStaff === 0) return 0;
  
  const recentAbsences = attendanceData
    .slice(-7)
    .filter(r => r.status === 'absent').length;
  
  return Math.min(100, Math.round((recentAbsences / (totalStaff * 7)) * 500));
};

const findPeakDay = (attendanceData) => {
  const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
  const dayCounts = days.map(() => 0);
  
  attendanceData.forEach(record => {
    const date = new Date(record.date);
    const dayOfWeek = date.getDay();
    if (record.status === 'present') {
      dayCounts[dayOfWeek]++;
    }
  });
  
  const maxIndex = dayCounts.indexOf(Math.max(...dayCounts));
  return days[maxIndex];
};

const groupByDay = (attendanceData) => {
  return attendanceData.reduce((acc, record) => {
    const date = record.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(record);
    return acc;
  }, {});
};

const calculateDepartmentPerformance = (attendanceData, staffData) => {
  const departments = [...new Set(staffData.map(s => s.department).filter(Boolean))];
  
  return departments.map(dept => {
    const deptStaff = staffData.filter(s => s.department === dept);
    const deptStaffIds = deptStaff.map(s => s.id);
    const deptAttendance = attendanceData.filter(a => deptStaffIds.includes(a.employeeId));
    
    const presentCount = deptAttendance.filter(a => a.status === 'present').length;
    const attendanceRate = deptAttendance.length > 0 ? (presentCount / deptAttendance.length) * 100 : 0;
    
    return {
      department: dept,
      attendanceRate: Math.round(attendanceRate)
    };
  });
};

const identifyRiskFactors = (attendanceData, staffData) => {
  const factors = [];
  
  // Аналіз відвідуваності по відділах
  const deptPerformance = calculateDepartmentPerformance(attendanceData, staffData);
  const lowPerformingDept = deptPerformance.find(dept => dept.attendanceRate < 75);
  if (lowPerformingDept) {
    factors.push({
      description: `Low attendance in ${lowPerformingDept.department} department (${lowPerformingDept.attendanceRate}%)`,
      impact: "High",
      severity: "high"
    });
  }
  
  // Аналіз загальної відвідуваності
  const totalStaff = staffData.filter(s => s.status === 'active').length;
  const recentPresent = attendanceData.slice(-7).filter(a => a.status === 'present').length;
  const overallRate = (recentPresent / (totalStaff * 7)) * 100;
  
  if (overallRate < 80) {
    factors.push({
      description: `Overall attendance rate is low (${Math.round(overallRate)}%)`,
      impact: "Medium",
      severity: "medium"
    });
  }
  
  // Додавання стандартних факторів
  factors.push(
    {
      description: "Multiple employees on vacation next week",
      impact: "Low",
      severity: "low"
    },
    {
      description: "Seasonal workload increase expected",
      impact: "Medium",
      severity: "medium"
    }
  );
  
  return factors;
};

// Допоміжні функції для AI рекомендацій
const calculateOverallAttendance = (attendanceData, totalStaff) => {
  if (attendanceData.length === 0 || totalStaff === 0) return 100;
  const presentCount = attendanceData.filter(a => a.status === 'present').length;
  return (presentCount / attendanceData.length) * 100;
};

const analyzeShiftDistribution = (shiftsData, staffData) => {
  // Спрощений аналіз розподілу змін
  const totalShifts = shiftsData.length;
  const morningShifts = shiftsData.filter(s => s.shiftType === 'M').length;
  const eveningShifts = shiftsData.filter(s => s.shiftType === 'T').length;
  
  const expectedRatio = 0.5; // Очікуваний розподіл 50/50
  const actualRatio = morningShifts / totalShifts;
  const imbalance = Math.abs(actualRatio - expectedRatio) * 100;
  
  return { imbalance: Math.round(imbalance) };
};

const analyzeDepartmentPerformance = (attendanceData, staffData) => {
  return calculateDepartmentPerformance(attendanceData, staffData);
};

// Компонент аналітики по відділах
const DepartmentAnalytics = ({ departmentStats, t }) => (
  <Box>
    <Typography variant="h6" sx={{ mb: 3 }}>{t('analytics.department_performance')}</Typography>
    <Grid container spacing={3}>
      {Object.values(departmentStats).map((dept) => (
        <Grid item xs={12} md={6} lg={4} key={dept.department}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{dept.department}</Typography>
              <Chip 
                label={`${dept.scheduleAdherence}%`}
                color={
                  dept.scheduleAdherence >= 95 ? 'success' :
                  dept.scheduleAdherence >= 85 ? 'warning' : 'error'
                }
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{t('analytics.employees')}:</Typography>
                <Typography variant="body2" fontWeight="bold">{dept.total}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{t('analytics.present')}:</Typography>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                  {dept.present}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{t('analytics.absent')}:</Typography>
                <Typography variant="body2" fontWeight="bold" color="error.main">
                  {dept.absent}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{t('analytics.scheduled_shifts')}:</Typography>
                <Typography variant="body2" fontWeight="bold">{dept.scheduledShifts}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{t('analytics.adherence')}:</Typography>
                <Typography variant="body2" fontWeight="bold">{dept.scheduleAdherence}%</Typography>
              </Box>
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={dept.scheduleAdherence}
              color={
                dept.scheduleAdherence >= 95 ? 'success' :
                dept.scheduleAdherence >= 85 ? 'warning' : 'error'
              }
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

// Компонент аналітики продуктивності
const PerformanceAnalytics = ({ topPerformers, t }) => (
  <Box>
    <Typography variant="h6" sx={{ mb: 3 }}>{t('analytics.top_performers')}</Typography>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>{t('analytics.employee')}</TableCell>
            <TableCell>{t('analytics.department')}</TableCell>
            <TableCell align="center">{t('analytics.attendance_rate')}</TableCell>
            <TableCell align="center">{t('analytics.schedule_adherence')}</TableCell>
            <TableCell align="center">{t('analytics.shifts_completed')}</TableCell>
            <TableCell align="center">{t('analytics.performance_score')}</TableCell>
            <TableCell align="center">{t('analytics.performance')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {topPerformers.map((employee, index) => (
            <TableRow key={employee.id} hover>
              <TableCell>
                <Typography fontWeight="500">{index + 1}</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="500">
                  {employee.name || `${employee.firstName} ${employee.lastName}`}
                </Typography>
              </TableCell>
              <TableCell>{employee.department}</TableCell>
              <TableCell align="center">
                <Chip 
                  label={`${employee.attendanceRate}%`}
                  color={
                    employee.attendanceRate >= 95 ? 'success' :
                    employee.attendanceRate >= 85 ? 'warning' : 'error'
                  }
                  size="small"
                />
              </TableCell>
              <TableCell align="center">
                <Chip 
                  label={`${employee.scheduleAdherence}%`}
                  color={
                    employee.scheduleAdherence >= 95 ? 'success' :
                    employee.scheduleAdherence >= 85 ? 'warning' : 'error'
                  }
                  size="small"
                />
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight="500">{employee.totalShifts}</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight="500" color="primary.main">
                  {employee.performanceScore}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  {index < 3 && <Star sx={{ color: '#FFD700', fontSize: 20 }} />}
                  <Chip 
                    label={
                      index === 0 ? t('analytics.excellent') :
                      index < 3 ? t('analytics.great') :
                      index < 5 ? t('analytics.good') : t('analytics.average')
                    }
                    size="small"
                    color={
                      index === 0 ? 'success' :
                      index < 3 ? 'primary' :
                      index < 5 ? 'default' : 'default'
                    }
                    variant={index >= 5 ? "outlined" : "filled"}
                  />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

// Компонент щоденної аналітики
const DailyAnalytics = ({ dailyStats, t }) => (
  <Box>
    <Typography variant="h6" sx={{ mb: 3 }}>{t('analytics.daily_attendance')}</Typography>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('analytics.date')}</TableCell>
            <TableCell align="center">{t('analytics.scheduled')}</TableCell>
            <TableCell align="center">{t('analytics.present')}</TableCell>
            <TableCell align="center">{t('analytics.absent')}</TableCell>
            <TableCell align="center">{t('analytics.late')}</TableCell>
            <TableCell align="center">{t('analytics.coverage')}</TableCell>
            <TableCell align="center">{t('analytics.adherence')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dailyStats.map((day) => (
            <TableRow key={day.date} hover>
              <TableCell>
                <Typography fontWeight="500">
                  {new Date(day.date).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight="500">{day.scheduled}</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography color="success.main" fontWeight="500">
                  {day.present}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography color="error.main" fontWeight="500">
                  {day.absent}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography color="warning.main" fontWeight="500">
                  {day.late}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Chip 
                  label={`${day.attendanceRate}%`}
                  color={
                    day.attendanceRate >= 95 ? 'success' :
                    day.attendanceRate >= 85 ? 'warning' : 'error'
                  }
                  size="small"
                />
              </TableCell>
              <TableCell align="center">
                <Chip 
                  label={`${day.scheduleCoverage}%`}
                  color={
                    day.scheduleCoverage >= 95 ? 'success' :
                    day.scheduleCoverage >= 85 ? 'warning' : 'error'
                  }
                  size="small"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

// Компонент аналітики порушень
const ViolationsAnalytics = ({ violations, t }) => (
  <Box>
    <Typography variant="h6" sx={{ mb: 3 }}>{t('analytics.schedule_violations')}</Typography>
    {violations.length === 0 ? (
      <Alert severity="success">
        {t('analytics.no_violations')}
      </Alert>
    ) : (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('analytics.employee')}</TableCell>
              <TableCell>{t('analytics.department')}</TableCell>
              <TableCell>{t('analytics.date')}</TableCell>
              <TableCell align="center">{t('analytics.scheduled_shift')}</TableCell>
              <TableCell align="center">{t('analytics.actual_status')}</TableCell>
              <TableCell align="center">{t('analytics.severity')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {violations.map((violation, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  <Typography fontWeight="500">{violation.employeeName}</Typography>
                </TableCell>
                <TableCell>{violation.department}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(violation.date).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip label={violation.scheduledShift} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={violation.actualStatus}
                    color={
                      violation.actualStatus === 'absent' ? 'error' : 
                      violation.actualStatus === 'late' ? 'warning' : 'default'
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={violation.severity}
                    color={violation.severity === 'high' ? 'error' : 'warning'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

// Компонент детальної аналітики
const DetailsAnalytics = ({ overallStats, departmentStats, violations, t }) => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('analytics.performance_insights')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.avg_shifts_per_employee')}
            </Typography>
            <Typography variant="h6" color="primary.main">
              {overallStats.avgShiftsPerEmployee}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.total_scheduled_shifts')}
            </Typography>
            <Typography variant="h6">
              {overallStats.scheduledShifts}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.total_violations')}
            </Typography>
            <Typography variant="h6" color="error.main">
              {violations.length}
            </Typography>
          </Box>
        </Box>
      </Card>
    </Grid>
    <Grid item xs={12} md={6}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('analytics.department_summary')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {Object.values(departmentStats).map(dept => (
            <Box key={dept.department} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">{dept.department}</Typography>
              <Typography variant="body2" fontWeight="500">
                {dept.total} {t('analytics.employees')} • {dept.scheduleAdherence}%
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>
    </Grid>
  </Grid>
);

export default StaffAnalytics;